/**
 * 「账户录入 / 编辑」的**草稿模型与提交映射**（纯逻辑，**零 DOM**）。
 *
 * 依据：B 域 PRD **§5.1**（账户管理）· **§5.1.5**（字段表）· **§5.2**（信用卡最小集）·
 * 架构 **§4.2**（`category` 与 `contraKind` 互斥）· **§6.1**（契约唯一源）·
 * `DR-018`（归属人唯一字段）· `DR-021` §6.3（改归属人仅 owner）。
 *
 * ## 🔴 为什么抽出来（与 `policy-draft.ts` / `txn-draft.ts` 同因）
 *
 * 页面（`AccountsPage`）负责"长什么样"，本文件负责"**草稿 → 请求**"：
 * ① 可测（字段多、形态分支多，纯函数用**零依赖基座**直接断言，不必渲染）；
 * ② 🔴 **防双实现** —— 形态规则（品类互斥 / 信用卡四字段归属 / 账单日成对）
 *    **只调共享层内核** `checkAccountShape()`，本文件一句规则都不重写。
 *    服务端 `AccountService` 调的是**同一个函数**（`AC-B1.2`）。
 *
 * ## 🔴 三条"本文件刻意不做"的事（每一条都对应一条铁律）
 *
 * | 不做 | 为什么 |
 * |---|---|
 * | **不收 `type` 的编辑入口** | 一张储蓄卡不会"变成"信用卡。契约层 `UpdateAccountRequest` 就**没有** `type` ⇒ 编辑态把类型渲染成**只读文本**，草稿里也不放可改的 `type` 字段 |
 * | **不收 `ownerId`** | 改归属人是规格 §16 **唯一那项"仅 owner"的例外**，走专用路由 `PUT /accounts/:id/owner`。契约层 PATCH **刻意不收** `ownerId` ⇒ 编辑态归属人**只读展示**（本批不画改归属人入口 —— 它不是"编辑字段"，是另一个管理动作） |
 * | **不收 `initialBalance` 的编辑** | 🔴 `AC-B6.7` 红线：**无「直接改账户余额」路径**。初始余额是**录入期的事实值**、只在建账户时写一次；事后校准的唯一手段是**「调整」流水**。⇒ 编辑态把初始余额渲染成**只读文本**，草稿里不放它 |
 *
 * ## 归属人在「新建」时的处置
 *
 * B 域 PRD §5.1.5 原文：「**建账户表单必含「归属人」字段，默认 = 当前用户**」。
 * 但 🔴 **`CreateAccountRequest.ownerId` 是可空的**（缺省 = 当前登录用户）——
 * 这是**服务端的缺省**，端侧**不自己填当前用户 id**（端侧不该假设"我是谁"，
 * 那需要读会话并解析用户 id，等于把服务端的缺省逻辑抄一遍）。
 * ⇒ 新建态归属人**默认留空**，此时请求体**根本不含 `ownerId` 键**（= 缺省交给服务端）；
 * 用户显式选择时才带上。这样"默认 = 当前用户"这条规则**只有一个实现处**（服务端）。
 */

import {
  AccountPolicy,
  AccountType,
  ContraKind,
  InternalCategory,
  centsToYuanText,
  checkAccountShape,
  percentTextToRateE4,
  rateE4ToPercentText,
  yuanTextToCents,
  type AccountView,
  type CreateAccountRequest,
  type Cents,
  type LoanSubtype,
  type ContraTerms,
  type RateE4,
  type RepaymentMethod,
  type UpdateAccountRequest,
} from '@jiaying/shared';

/**
 * 一张账户表单的完整状态。
 *
 * ⚠️ **全部字段都是"用户输入态"**：
 * - 金额是文本（`"0"` / `"5000.00"`）—— 换算在 `buildAccountRequest()` 里做，表单不做算术；
 * - 品类 / 事项类型是**取值或 `''`**（空串 = 未选，而不是 `null` —— 受控 `TextField select` 的
 *   value 为空串才是"未选"的合法形态）；
 * - 数字型字段（额度 / 账单日 / 还款日）也是文本（输入框天然给字符串）。
 */
export interface AccountDraft {
  /** 账户名（必填） */
  name: string;
  /** 🔴 **仅新建可改**；编辑态由 `draftFromView()` 原样带上，表单渲染成只读 */
  type: AccountType;
  /** `internal` 品类；`''` = 未选（仅 `type='internal'` 时有值） */
  category: InternalCategory | '';
  /** `contra` 事项类型；`''` = 未选（仅 `type='contra'` 时有值） */
  contraKind: ContraKind | '';
  /**
   * 归属人 id；`''` = **不指定**（新建时交给服务端缺省 = 当前用户；编辑时不提交）。
   * 🔴 端侧**不自己填当前用户 id** —— 见文件头「归属人在新建时的处置」。
   */
  ownerId: string;
  /** ISO 4217；新建默认 `CNY`（P0 固定 CNY，结构支持多币种） */
  currency: string;
  /** 初始余额（元，文本）；🔴 **仅新建**（编辑态只读，见文件头表格第 3 行） */
  initialBalanceText: string;
  /** 是否在「日常账户」之外收起（社保账户默认 `true`） */
  hidden: boolean;
  // ── 信用卡四字段（文本；仅 `type='credit'` 时提交）──
  /** 信用额度（元，文本） */
  creditLimitText: string;
  /** 账单日（`1`–`28` 的文本） */
  billingDayText: string;
  /** 还款日（`1`–`28` 的文本） */
  repaymentDayText: string;
  /** 卡号后四位 */
  tailNumber: string;
  // ── 🆕 预付卡次卡两项（`DR-057`；仅 `internal(prepaid)` 时提交）──
  /** 总次数（文本；`''` = 未登记） */
  passTotalText: string;
  /** 单次金额（元，文本；`''` = 未登记） */
  passUnitAmountText: string;
  // ── 🆕 对手方（`DR-046`；仅 `type='contra'` 时提交）──
  /** 对手方（跟谁）；`''` = 未填 ⇒ 提交 `null` */
  counterparty: string;
  // ── 🆕 贷款六参数（`DR-044`；仅 `type='contra'` 且 `contraKind='loan'` 时提交）──
  //
  // 🔴 **全部可空**，`''` = 未填 ⇒ 提交 `null`（`DR-044` §5 第 2 项拍板）。
  // 🔴 年利率在草稿里是**百分数文本**（`"3.36"`），换 `E4` 走内核 `percentTextToRateE4()`
  //    —— 本文件不做 ×100 的算术（禁浮点 + 一个机制一个实现处）。
  /** 到期日（`YYYY-MM-DD` 文本；空 = 未填） */
  dueDateText: string;
  /** 年利率（**百分数**文本，最多两位小数；空 = 未填） */
  annualRatePercentText: string;
  /** 总期数（整数文本；空 = 未填） */
  termsText: string;
  /** 起息日（`YYYY-MM-DD` 文本；空 = 未填） */
  startDateText: string;
  /** 还款方式；`''` = 未选 */
  method: RepaymentMethod | '';
  /** 贷款子类型；`''` = 未选 */
  subtype: LoanSubtype | '';
}

/** 空草稿（新建）—— 默认 `debit` / `CNY` / 不隐藏 */
export function emptyAccountDraft(): AccountDraft {
  return {
    name: '',
    // 最常见的建账户场景是"加一张卡"⇒ 默认储蓄卡（与 B 域 PRD §5.4 冷启动的 3 个日常账户一致）
    type: AccountType.Debit,
    category: '',
    contraKind: '',
    ownerId: '',
    currency: 'CNY',
    initialBalanceText: '0',
    hidden: false,
    creditLimitText: '',
    billingDayText: '',
    repaymentDayText: '',
    tailNumber: '',
    passTotalText: '',
    passUnitAmountText: '',
    counterparty: '',
    dueDateText: '',
    annualRatePercentText: '',
    termsText: '',
    startDateText: '',
    method: '',
    subtype: '',
  };
}

/**
 * 编辑：`AccountView` → 草稿。
 *
 * 🔴 **金额回填走内核 `centsToYuanText()`**，不自己写 `/ 100` ——
 * ESLint 块 A 把 `/`、`toFixed`、`Intl.NumberFormat` 全项目封死、只留 `nucleus/money.ts` 白名单。
 *
 * 🔴 **数字型字段的处置**：服务端的 `billingDay` 等是 `number | null`；
 * 草稿是文本 ⇒ `null` → `''`（空 = 未填），有值 → `String(n)`。
 * 反向在 `buildAccountRequest()` 里 `''` → `null`。**不要用 `String(null)`**（会得到 `"null"`）。
 */
export function draftFromView(view: AccountView): AccountDraft {
  return {
    name: view.name,
    // 🔴 带上类型只为了**渲染只读文本**与让内核能判定形态；它不会进 PATCH 请求体
    type: view.type,
    category: view.category ?? '',
    contraKind: view.contraKind ?? '',
    ownerId: view.ownerId,
    currency: view.currency,
    initialBalanceText: centsToYuanText(view.initialBalance as Cents),
    hidden: view.hidden,
    creditLimitText: view.credit?.creditLimit === null || view.credit === null
      ? ''
      : centsToYuanText(view.credit.creditLimit as Cents),
    billingDayText: view.credit?.billingDay === null || view.credit === null
      ? ''
      : String(view.credit.billingDay),
    repaymentDayText: view.credit?.repaymentDay === null || view.credit === null
      ? ''
      : String(view.credit.repaymentDay),
    tailNumber: view.credit?.tailNumber ?? '',
    // 🆕 次卡两项（`DR-057`）：`null` → `''`（空 = 未登记）
    passTotalText: view.pass === null ? '' : String(view.pass.total),
    passUnitAmountText:
      view.pass === null ? '' : centsToYuanText(view.pass.unitAmount as Cents),
    counterparty: view.counterparty ?? '',
    // 🆕 贷款六参数（`DR-044`）：`null` → `''`（空 = 未填）；利率回填走内核（不做 /100）
    dueDateText: view.contraTerms?.dueDate ?? '',
    annualRatePercentText:
      view.contraTerms?.annualRateE4 === null || view.contraTerms === null
        ? ''
        : rateE4ToPercentText(view.contraTerms.annualRateE4 as RateE4),
    termsText: view.contraTerms?.terms === null || view.contraTerms === null ? '' : String(view.contraTerms.terms),
    startDateText: view.contraTerms?.startDate ?? '',
    method: view.contraTerms?.method ?? '',
    subtype: view.contraTerms?.subtype ?? '',
  };
}

// ═════════════════════════════ 草稿 → 请求 ═════════════════════════════

export type AccountBuildResult<R> =
  | { readonly ok: true; readonly request: R }
  | { readonly ok: false; readonly errors: readonly string[] };

const BAD_AMOUNT = '金额须为数字，最多两位小数（不要带千分位与币种符号）';
// 🔴 区间**从 `AccountPolicy` 取**，不写字面量 —— 否则放宽上界时要改两处（铁律 24：一个机制一个实现处）
const BAD_DAY = (label: string): string =>
  `${label}须为 ${AccountPolicy.BillingDayMin}–${AccountPolicy.BillingDayMax} 的整数日`;
const BAD_LIMIT = '信用额度须为大于 0 的数字，最多两位小数';
// 🆕 贷款参数（`DR-044`）—— 上限同样**从 `AccountPolicy` 取**（不写字面量）；
//    🔴 提示语里的百分数**经内核 `rateE4ToPercentText()` 反算** —— 本文件不想写 `/100`（禁浮点）
const BAD_RATE = `年利率须为 0–${rateE4ToPercentText(AccountPolicy.AnnualRateE4Max as RateE4)} 的数字，最多两位小数（如 3.36）`;
const BAD_TERMS = `期数须为 1–${String(AccountPolicy.TermsMax)} 的整数（总期数；未知请留空）`;

/**
 * 元文本 → 整数分；**非法返回 `null`**（不抛）。
 *
 * 🔴 "先判后转"：`yuanTextToCents` 对非法文本抛 `RangeError`（内核防御），
 * 表单侧自己接住它 —— 与 `txn-draft.ts` / `policy-draft.ts` 同约定。
 *
 * ⚠️ 负数是**合法输入**（初始余额允许为负，如"这张卡现在就欠着"）⇒ 正则含 `-?`。
 */
function toCents(text: string): Cents | null {
  const trimmed = text.trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  try {
    return yuanTextToCents(trimmed);
  } catch {
    return null;
  }
}

/** 文本 → `number | null`（空串 = `null`；非整数文本 = `undefined` 表示"非法"） */
function toNullableInt(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return undefined;
  return Number.parseInt(trimmed, 10);
}

/**
 * 🆕 贷款六参数：草稿 → 内核入参（`DR-044`）。
 *
 * 🔴 **非「`contra` + `loan`」一律归零**（与 `shapeOf` 对形态字段同一条原则）——
 * 用户先选了贷款、填了利率，又把事项类型改成"应付"：残留值**既不该报错、也不该提交**。
 *
 * 🔴 解析失败**只报错、不猜**：`3.575`（三位小数）与 `abc` 都是错误，
 * 不会被静默截断或悄悄置空 —— 用户以为存进去了、实际没存，是最坏的一类失败。
 *
 * ⚠️ 年利率的"百分数 → `E4`"换算**只经内核** `percentTextToRateE4()`（禁浮点 · 铁律 24）：
 * 本文件一句 `× 100` 都不写。
 */
function parseContraTerms(draft: AccountDraft): { contraTerms: ContraTerms; errors: string[] } {
  const empty: ContraTerms = {
    dueDate: null,
    annualRateE4: null,
    terms: null,
    startDate: null,
    method: null,
    subtype: null,
  };
  // 🔴 **两层归属**（`DR-044` D1 + v1 实测）：`dueDate` 对所有 `contra` 有效
  //    （**应收 / 应付也有到期日** —— v1 实测就有）；其余五项仅「contra + loan」。
  if (draft.type !== AccountType.Contra) return { contraTerms: empty, errors: [] };
  const isLoan = draft.contraKind === ContraKind.Loan;

  const errors: string[] = [];

  // ── 年利率（百分数文本 → E4；仅贷款）──
  const rateText = isLoan ? draft.annualRatePercentText.trim() : '';
  let annualRateE4: number | null = null;
  if (rateText !== '') {
    try {
      annualRateE4 = percentTextToRateE4(rateText);
    } catch {
      errors.push(BAD_RATE);
    }
  }

  // ── 总期数（正整数；留空合法；仅贷款）──
  const termsText = isLoan ? draft.termsText.trim() : '';
  let terms: number | null = null;
  if (termsText !== '') {
    const n = toNullableInt(termsText);
    // 🔴 `0` 也是错误 —— 留空表达"未知"，不该用 0 表示（`DR-044` §5 第 2 项）
    if (n === undefined || n === null || n < 1) errors.push(BAD_TERMS);
    else terms = n;
  }

  // ── 两个日期（`YYYY-MM-DD`；留空合法）──
  const parseDay = (text: string, label: string): string | null => {
    const t = text.trim();
    if (t === '') return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      errors.push(`${label}格式须为 YYYY-MM-DD`);
      return null;
    }
    return t;
  };

  return {
    contraTerms: {
      dueDate: parseDay(draft.dueDateText, '到期日'),
      annualRateE4,
      terms,
      startDate: isLoan ? parseDay(draft.startDateText, '起息日') : null,
      method: isLoan && draft.method !== '' ? draft.method : null,
      subtype: isLoan && draft.subtype !== '' ? draft.subtype : null,
    },
    errors,
  };
}

/**
 * 把草稿的**形态字段**摊成内核入参。
 *
 * 🔴 关键：**非本类型的字段一律归零**（不是"原样带上"）。
 * 例：`type='debit'` 时即使草稿里残留 `category` 也传 `null` —— 否则切类型后
 * 残留值会让内核报"type=debit 不得指定 category"，而那**不是用户的问题**
 * （用户只是先选了 internal 又改成 debit）。归零发生在**一处**（本函数），
 * 表单只需忠实记录用户点过什么。
 *
 * 🆕 贷款六参数同样在此摊平（由 `parseContraTerms()` 先归零 + 解析后传入）——
 * 内核 `checkAccountShape()` 需要它们才能判"该不该在这类账户上出现"。
 */
function shapeOf(draft: AccountDraft, creditLimit: number | null, contraTerms: ContraTerms): {
  type: AccountType;
  category: InternalCategory | null;
  contraKind: ContraKind | null;
  creditLimit: number | null;
  billingDay: number | null;
  repaymentDay: number | null;
  tailNumber: string | null;
  /** 🆕 对手方（`DR-046`）—— 非 `contra` 时归零 */
  counterparty: string | null;
  contraTerms: ContraTerms;
  /** 🆕 次卡两项（`DR-057`）—— 非 `internal(prepaid)` 时归零 */
  passTotalTimes: number | null;
  passUnitAmount: number | null;
} {
  const isInternal = draft.type === AccountType.Internal;
  const isContra = draft.type === AccountType.Contra;
  const isCredit = draft.type === AccountType.Credit;
  /** 🆕 次卡只对预付卡有语义（`DR-057` D4.2）—— 与内核第 13 条是同一条规则 */
  const isPrepaid = isInternal && draft.category === InternalCategory.Prepaid;

  return {
    type: draft.type,
    category: isInternal && draft.category !== '' ? draft.category : null,
    contraKind: isContra && draft.contraKind !== '' ? draft.contraKind : null,
    creditLimit: isCredit ? creditLimit : null,
    billingDay: isCredit ? (toNullableInt(draft.billingDayText) ?? null) : null,
    repaymentDay: isCredit ? (toNullableInt(draft.repaymentDayText) ?? null) : null,
    tailNumber: isCredit && draft.tailNumber.trim() !== '' ? draft.tailNumber.trim() : null,
    // 🆕 次卡两项（`DR-057`）：与信用卡四列同处置 —— **非预付卡时归零**，
    //    让"切类型后残留的值"不成为用户的错（服务端内核第 13 条同样会拦）
    passTotalTimes: isPrepaid ? (toNullableInt(draft.passTotalText) ?? null) : null,
    passUnitAmount: isPrepaid ? toCents(draft.passUnitAmountText) : null,
    // 🆕 对手方（`DR-046`）：非 `contra` **归零**（与服务端内核第 11 条同一语义 ——
    //    端侧归零是为了让「切类型后残留值」不变成用户的错，见文件头「归零发生在一处」）
    counterparty: isContra && draft.counterparty.trim() !== '' ? draft.counterparty.trim() : null,
    // 🆕 期限块六项：由 `parseContraTerms()` 统一归零（非 `contra` 时它已给出全 `null`）
    //    ⇒ 本函数**不再判一次**「是不是贷款」（否则同一条规则长出第二份实现）
    contraTerms,
  };
}

/**
 * 草稿 → `POST /api/accounts` 的请求体（**新建**）。
 *
 * 校验分两层（与 `policy-draft.ts` 同结构）：
 * ① **UI 可用性层**（本文，让按钮可用性与就地提示成立）—— 名称非空、金额可解析；
 * ② **语义层**（共享层内核 `checkAccountShape`）—— 品类互斥 / 信用卡字段归属 / 账单日成对。
 */
export function buildCreateAccountRequest(draft: AccountDraft): AccountBuildResult<CreateAccountRequest> {
  const errors: string[] = [];

  // ── ① 逐字段（UI 可用性；权威在契约层 `CreateAccountRequestSchema`）──
  if (draft.name.trim() === '') errors.push('账户名必填');
  if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) errors.push('币种须为 ISO 4217 三字母代码（如 CNY）');

  const initialBalance = toCents(draft.initialBalanceText);
  if (initialBalance === null) errors.push(`初始余额：${BAD_AMOUNT}`);

  // ── ② 信用卡四字段（文本 → 数字）──
  const isCredit = draft.type === AccountType.Credit;
  let creditLimit: number | null = null;
  if (isCredit) {
    const trimmedLimit = draft.creditLimitText.trim();
    if (trimmedLimit !== '') {
      creditLimit = toCents(trimmedLimit);
      if (creditLimit === null) errors.push(BAD_LIMIT);
    }
    for (const [text, label] of [
      [draft.billingDayText, '账单日'],
      [draft.repaymentDayText, '还款日'],
    ] as const) {
      const n = toNullableInt(text);
      if (n === undefined) errors.push(BAD_DAY(label));
      else if (n !== null && (n < AccountPolicy.BillingDayMin || n > AccountPolicy.BillingDayMax)) errors.push(BAD_DAY(label));
    }
  }

  if (initialBalance === null) return { ok: false, errors };
  if (creditLimit !== null && creditLimit <= 0) {
    errors.push(BAD_LIMIT);
    return { ok: false, errors };
  }

  // ── 🆕 贷款六参数（`DR-044`；非「contra + loan」时全部归零）──
  const termsParsed = parseContraTerms(draft);
  errors.push(...termsParsed.errors);

  if (errors.length > 0) return { ok: false, errors };

  const shape = shapeOf(draft, creditLimit, termsParsed.contraTerms);

  // ── ③ 🔴 语义判定：**只调内核**（品类互斥 / 字段归属 / 账单日成对 / 额度为正 / 贷款参数归属）──
  const issues = checkAccountShape(shape);
  if (issues.length > 0) return { ok: false, errors: issues };

  const ownerId = draft.ownerId.trim();

  return {
    ok: true,
    request: {
      name: draft.name.trim(),
      type: draft.type,
      category: shape.category,
      contraKind: shape.contraKind,
      // 🔴 空 = **不指定** ⇒ 交给服务端缺省（当前用户）。见文件头「归属人在新建时的处置」。
      //    关键：用**展开合并**而不是 `ownerId: undefined` —— 后者在 JS 里**键仍然存在**
      //    （`'ownerId' in req === true`，值 undefined）。序列化成 JSON 时键会消失，
      //    但契约对象在端侧还会被别的代码读 ⇒ "不传"必须在**对象层面**成立。
      ...(ownerId === '' ? {} : { ownerId }),
      currency: draft.currency.trim().toUpperCase(),
      initialBalance,
      hidden: draft.hidden,
      creditLimit: shape.creditLimit,
      billingDay: shape.billingDay,
      repaymentDay: shape.repaymentDay,
      tailNumber: shape.tailNumber,
      // 🆕 次卡两项（`DR-057`）—— 显式带上（含 `null` = 清空），与上面的 `category` 同约定：
      //    "不传"与"传 null"在 PATCH 里语义不同，所以这里不省键
      passTotalTimes: shape.passTotalTimes,
      passUnitAmount: shape.passUnitAmount,
      counterparty: shape.counterparty,
      // 🆕 贷款六参数（`DR-044`）—— 非贷款账户时是全 `null`（刻意**显式带上**，
      //    让服务端内核第 8 条能判"不该有的参数被给了"而不是被静默丢弃）
      ...shape.contraTerms,
    },
  };
}

/**
 * 草稿 → `PATCH /api/accounts/:id` 的请求体（**编辑**）。
 *
 * 🔴 **不含 `type` / `ownerId` / `initialBalance`**（每一样都对应一条铁律，见文件头表格）。
 *
 * 字段语义 = **传了就替换**（与流水编辑同约定）；`null` 表示"清空该字段"。
 * ⇒ 本函数**总是把形态字段显式带上**（含 `null`），不依赖"省略即不改" ——
 * 省略会让用户"把品类从待缴改成预付"这种操作**静默不生效**（PATCH 只收到 `{name}`）。
 */
export function buildUpdateAccountRequest(draft: AccountDraft): AccountBuildResult<UpdateAccountRequest> {
  const errors: string[] = [];

  if (draft.name.trim() === '') errors.push('账户名必填');
  if (!/^[A-Za-z]{3}$/.test(draft.currency.trim())) errors.push('币种须为 ISO 4217 三字母代码（如 CNY）');

  const isCredit = draft.type === AccountType.Credit;
  let creditLimit: number | null = null;
  if (isCredit) {
    const trimmedLimit = draft.creditLimitText.trim();
    if (trimmedLimit !== '') {
      creditLimit = toCents(trimmedLimit);
      if (creditLimit === null) errors.push(BAD_LIMIT);
    }
    for (const [text, label] of [
      [draft.billingDayText, '账单日'],
      [draft.repaymentDayText, '还款日'],
    ] as const) {
      const n = toNullableInt(text);
      if (n === undefined) errors.push(BAD_DAY(label));
      else if (n !== null && (n < AccountPolicy.BillingDayMin || n > AccountPolicy.BillingDayMax)) errors.push(BAD_DAY(label));
    }
  }

  if (creditLimit !== null && creditLimit <= 0) errors.push(BAD_LIMIT);

  // ── 🆕 贷款六参数（`DR-044`）—— PATCH 语义：**传了就替换**，未填即 `null`（清空）──
  //    ⚠️ 与 `create` 同一处解析（`parseContraTerms`），不写第二遍
  const termsParsed = parseContraTerms(draft);
  errors.push(...termsParsed.errors);

  if (errors.length > 0) return { ok: false, errors };

  const shape = shapeOf(draft, creditLimit, termsParsed.contraTerms);

  // ── 🔴 语义判定：同一个内核（服务端 PATCH 路径也调它）──
  const issues = checkAccountShape(shape);
  if (issues.length > 0) return { ok: false, errors: issues };

  return {
    ok: true,
    request: {
      name: draft.name.trim(),
      // 🔴 不是"仅 internal 才传"：传 `null` 才能让"从 internal 改成…"这类清空生效
      //    （虽然 type 不可改，但用户可以在编辑态把品类取消选择 ⇒ 必须能表达"清空"）
      category: shape.category,
      contraKind: shape.contraKind,
      currency: draft.currency.trim().toUpperCase(),
      hidden: draft.hidden,
      creditLimit: shape.creditLimit,
      billingDay: shape.billingDay,
      repaymentDay: shape.repaymentDay,
      tailNumber: shape.tailNumber,
      // 🆕 次卡两项（`DR-057`）—— 显式带上（含 `null` = 清空），与上面的 `category` 同约定：
      //    "不传"与"传 null"在 PATCH 里语义不同，所以这里不省键
      passTotalTimes: shape.passTotalTimes,
      passUnitAmount: shape.passUnitAmount,
      counterparty: shape.counterparty,
      // 🆕 贷款六参数：显式带上（含 `null` = 清空），理由同上面的 `category`
      ...shape.contraTerms,
    },
  };
}
